<template>
  <div>
    <transition name="fade">
      <location-remove-popup
        v-if="showRemoveLocation"
        :location="locationBeingEditedRemoved"
        @close="showRemoveLocation = false"
        @location-removed="$emit('reload-locations')"
      />
      <location-add-edit
        v-if="showEditLocation"
        :location="locationBeingEditedRemoved"
        :already-used-colors="locations.map(loc => loc.color)"
        :already-used-workplace-names="locations.map(loc => loc.name).filter(name => name != locationBeingEditedRemoved.name)"
        :edit="true"
        :color-prop="color"
        :team-id="team.teamId"
        @close="showEditLocation = false"
        @save="$emit('reload-locations')"
      />
    </transition>
    <div class="locations-list">
      <div class="locations-header">
        <div class="location-header name">
          {{ $t('name') }}
        </div>
        <div class="location-header location">
          {{ $t('location') }}
        </div>
        <div class="location-header color">
          {{ $t('color') }}
        </div>
        <div class="location-header" />
      </div>
      <div
        v-for="location in filteredLocations"
        :key="location.id"
        class="location-row"
      >
        <div class="location-item name">
          {{ location.name }}
        </div>
        <div class="location-item timezone">
          {{ getTimezoneLocation(location) }}
        </div>
        <div
          class="location-item color-item"
          :style="{ color: location.color }"
        >
          <span class="color-label">{{ locationColorNames[location.color] }}</span>
        </div>
        <div class="location-item controls">
          <div>
            <div
              class="clavo-link primary bold"
              @click="editLoc(location)"
            >
              {{ $t('edit') }}
            </div>
            <div
              class="clavo-link danger bold"
              @click="removeLoc(location)"
            >
              {{ $t('remove') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="filteredLocations.length === 0"
      class="no-results"
    >
      <img
        src="@/assets/illustrations/sloth-sleeping-gray-blue.svg"
        alt="Sleeping sloth"
      >
      <div>{{ $t('sorryNoResultsFound') }}</div>
    </div>
  </div>
</template>

<script>
import Color from '@/components/Color'
import LocationAddEdit from '@/components/Location/AddEdit'
import LocationRemovePopup from '@/components/Location/LocationRemovePopup'
import ParseFunctions from '@/components/ParseFunctions'

export default {
  components: {
    LocationRemovePopup,
    LocationAddEdit,
  },
  mixins: [Color, ParseFunctions],
  props: {
    //teamId: String // private locations so no need for team id
    searchedName: String,
    locations: Array,
    locationColorNames: Object,
    team: Object,
  },
  data() {
    return {
      showRemoveLocation: false,
      showEditLocation: false,
      locationBeingEditedRemoved: null,
      color: null,
    }
  },
  computed: {
    filteredLocations() {
      return this.locations.filter(loc => {
        return this.searchedName === '' || this.searchedName === undefined || loc.name.toLowerCase().includes(this.searchedName.toLowerCase())
      })
    },
  },
  methods: {
    async removeLoc(loc) {
      this.locationBeingEditedRemoved = loc
      this.showRemoveLocation = true
    },
    editLoc(loc) {
      this.locationBeingEditedRemoved = loc
      this.showEditLocation = true
      this.color = loc.color
    },
    getTimezoneLocation(loc){
      if (loc.timezone && loc.singleLocation) {
        let timezone = this.$store.state.timezonesGlobal.find(x => x.value === loc.timezone)
        if(!timezone)
          return loc.timezone
        return timezone.name + ' ' + (timezone.utc || '')
      } else {
        return this.$t('userSelectedLocation')
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.locations-list {
  display: table;
  width: 100%;
  margin-bottom: 17px;

  .locations-header {
    display: table-header-group;
    position: sticky;
    top: 76px;
    background: white;
    @include respond-below(xs) {
      top: 128px
    }

    .location-header {
      @include label;
      text-transform: uppercase;
      padding: 12px 9px 12px 9px;
      display: table-cell;
      border-bottom: 1px solid $soap;
      border-top: 1px solid $soap;
      &:first-child {
        padding-left: 18px;
      }
      &:last-child {
        padding-right: 18px;
      }
      @include respond-above(sm) {
        &.name {
          width: 30%;
        }
        &.location {
          width: 30%;
        }
        &.color {
          width: 20%;
        }
      }
    }
  }

  .location-row {
    display: table-row;

    .location-item {
      padding: 21px 9px 21px 9px;
      display: table-cell;
      vertical-align: middle;
      line-height: 18px;
      &:first-child {
        padding-left: 18px;
      }
      &:last-child {
        padding-right: 18px;
      }

      @include respond-above(sm) {
        &.name {
          width: 30%;
          font-family: $bold;
        }
        &.timezone {
          width: 30%;
          @include font-rule('averta-regular');
        }
        &.color-item {
          width: 20%;
          @include font-rule('averta-regular');
        }
        &.controls {
          // width: 20%;
          div div:first-child {
            margin-right: 10px;
          }
        }
      }

      @include respond-below(sm) {
        &.name {
          width: 30%;
          font-family: $bold;
        }
        &.timezone {
        }
        &.color-item {
        }
        &.controls {
          padding-left: 0;

          div div:first-child {
            margin-right: 10px;
          }
        }
      }

      &.controls {
        div {
          display: flex;
          justify-content: space-around;
          button {
            border: 0;
            @include font-rule('averta-bold');
            font-size: 14px;
          }
          @include respond-below(xs) {
            flex-direction: column;
            div {
              margin: 5px 0;
            }
          }
        }
      }
      border-bottom: 1px solid $soap;
      font-size: 14px;
      font-weight: 500;

      &.color-item:before {
        font-size: 24px;
        line-height: 18px;
        vertical-align: top;
        content: '\25CF'; // circle
      }

      .color-label {
        margin-left: 5px;
        color: $black;
        vertical-align: middle;
        @include respond-below(sm) {
          display: none;
        }
      }
    }

    &:hover {
      background-color: $white-lilac;
      background-color: $white-lilac;
      .controls {
        div button {
          background-color: $white-lilac;
        }
      }
    }
  }
}
.no-results {
  text-align: center;
  img {
    width: 340px;
    margin-top: 96px;
    @include respond-below(sm) {
      width: 300px;
    }
  }
  div {
    text-align: center;
    font-family: $semibold;
    color: $bluebell-light;
    margin-top: 36px;
  }
}
</style>
